import { router } from '@inertiajs/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ConnectControllerButton } from '@/components/arcade/connect-controller-button';
import { useDeploymentRefresh } from '@/hooks/use-deployment-refresh';
import { useProControllers } from '@/hooks/use-pro-controllers';
import { proControllerToMenuGamepad } from '@/lib/pro-controller';
import type { ArcadeMachineGame } from '@/types';
import {
  type ArcadeNavigationDirection,
  KEY_MAP_P1,
  KEY_MAP_P2,
  nextGridIndex,
} from './arcade-machine-input';

interface ArcadeMachineProps {
  games: ArcadeMachineGame[];
}

function createClickSound() {
  const audioContext = new (
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext })
      .webkitAudioContext
  )();

  return () => {
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'square';

    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(
      0.001,
      audioContext.currentTime + 0.05,
    );

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.05);
  };
}

// Virtual gamepad state from keyboard input (default cabinet wiring).
interface VirtualGamepad {
  buttons: boolean[];
  axes: number[];
}

function createVirtualGamepads() {
  const gamepads: [VirtualGamepad, VirtualGamepad] = [
    { buttons: Array(17).fill(false), axes: [0, 0, 0, 0] },
    { buttons: Array(17).fill(false), axes: [0, 0, 0, 0] },
  ];

  const handleKey = (e: KeyboardEvent, pressed: boolean) => {
    const key = e.key.toLowerCase();

    const p1Map = KEY_MAP_P1[key];
    if (p1Map) {
      e.preventDefault();
      if (p1Map.type === 'button') {
        gamepads[0].buttons[p1Map.index] = pressed;
      } else if (p1Map.type === 'axis') {
        gamepads[0].axes[p1Map.index] = pressed ? (p1Map.value ?? 0) : 0;
      }
      return;
    }

    const p2Map = KEY_MAP_P2[key];
    if (p2Map) {
      e.preventDefault();
      if (p2Map.type === 'button') {
        gamepads[1].buttons[p2Map.index] = pressed;
      } else if (p2Map.type === 'axis') {
        gamepads[1].axes[p2Map.index] = pressed ? (p2Map.value ?? 0) : 0;
      }
      return;
    }

    if (key === 'escape') {
      e.preventDefault();
      gamepads[0].buttons[1] = pressed;
    }
  };

  const onKeyDown = (e: KeyboardEvent) => handleKey(e, true);
  const onKeyUp = (e: KeyboardEvent) => handleKey(e, false);

  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);

  return {
    getGamepads: () => gamepads,
    cleanup: () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    },
  };
}

const STORAGE_KEY = 'arcade-26-machine-selected-index';
const MAX_DESCRIPTION_LENGTH = 150;

export function ArcadeMachine({ games }: ArcadeMachineProps) {
  useDeploymentRefresh(5000);
  const {
    supported: hidSupported,
    count: hidCount,
    connect: hidConnect,
    snapshotsRef: hidSnapshotsRef,
  } = useProControllers();

  const [selectedIndex, setSelectedIndex] = useState(() => {
    if (typeof window === 'undefined') return 0;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const index = parseInt(stored, 10);
      if (!Number.isNaN(index) && index >= 0 && index < games.length) {
        return index;
      }
    }
    return 0;
  });
  const [showFullDescription, setShowFullDescription] = useState(false);
  const playClickRef = useRef<(() => void) | null>(null);
  const virtualGamepadsRef = useRef<ReturnType<
    typeof createVirtualGamepads
  > | null>(null);

  const gamesLengthRef = useRef(games.length);
  const selectedGameRef = useRef(games[selectedIndex]);

  gamesLengthRef.current = games.length;
  selectedGameRef.current = games[selectedIndex];

  const selectedGame = games[selectedIndex];

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, selectedIndex.toString());
  }, [selectedIndex]);

  const selectedTileRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    selectedTileRef.current?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    });
  }, [selectedIndex]);

  useEffect(() => {
    playClickRef.current = createClickSound();
    virtualGamepadsRef.current = createVirtualGamepads();

    return () => {
      virtualGamepadsRef.current?.cleanup();
    };
  }, []);

  const playClickSound = useCallback(() => {
    playClickRef.current?.();
  }, []);

  const navigate = useCallback((direction: ArcadeNavigationDirection) => {
    setSelectedIndex((prev) => {
      const newIndex = nextGridIndex(prev, direction, gamesLengthRef.current);

      if (newIndex !== prev) {
        playClickRef.current?.();
        setShowFullDescription(false);
      }
      return newIndex;
    });
  }, []);

  const toggleDescription = useCallback(() => {
    setShowFullDescription((prev) => !prev);
  }, []);

  const handleSelect = useCallback(() => {
    if (selectedGameRef.current) {
      router.visit(`/26/arcade/machine/${selectedGameRef.current.slug}`);
    }
  }, []);

  useEffect(() => {
    let animationId: number;
    const lastButtons: [boolean[], boolean[]] = [
      Array(17).fill(false),
      Array(17).fill(false),
    ];
    const lastAxes: [boolean[], boolean[]] = [
      [false, false, false, false],
      [false, false, false, false],
    ];
    const DEADZONE = 0.5;

    const pollGamepad = () => {
      const realGamepads = navigator.getGamepads();
      const virtualGamepads = virtualGamepadsRef.current?.getGamepads() ?? [];

      // Decoded Pro Controllers (WebHID) mapped onto the standard-gamepad layout,
      // indexed by player slot so each controller drives the menu like a pad.
      const hidGamepads: Array<
        { buttons: boolean[]; axes: number[] } | undefined
      > = [];
      for (const snapshot of hidSnapshotsRef.current) {
        const slot = snapshot.player - 1;
        if (slot === 0 || slot === 1) {
          hidGamepads[slot] = proControllerToMenuGamepad(snapshot);
        }
      }

      for (let playerIndex = 0; playerIndex < 2; playerIndex++) {
        const realGp = realGamepads[playerIndex];
        const virtualGp = virtualGamepads[playerIndex];
        const hidGp = hidGamepads[playerIndex];

        const buttons: boolean[] = Array(17).fill(false);
        const axes: number[] = [0, 0, 0, 0];

        if (realGp) {
          for (let i = 0; i < realGp.buttons.length; i++) {
            buttons[i] = buttons[i] || realGp.buttons[i].pressed;
          }
          for (let i = 0; i < realGp.axes.length; i++) {
            if (Math.abs(realGp.axes[i]) > Math.abs(axes[i])) {
              axes[i] = realGp.axes[i];
            }
          }
        }

        if (virtualGp) {
          for (let i = 0; i < virtualGp.buttons.length; i++) {
            buttons[i] = buttons[i] || virtualGp.buttons[i];
          }
          for (let i = 0; i < virtualGp.axes.length; i++) {
            if (Math.abs(virtualGp.axes[i]) > Math.abs(axes[i])) {
              axes[i] = virtualGp.axes[i];
            }
          }
        }

        if (hidGp) {
          for (let i = 0; i < hidGp.buttons.length; i++) {
            buttons[i] = buttons[i] || hidGp.buttons[i];
          }
          for (let i = 0; i < hidGp.axes.length; i++) {
            if (Math.abs(hidGp.axes[i]) > Math.abs(axes[i])) {
              axes[i] = hidGp.axes[i];
            }
          }
        }

        const wasPressed = (index: number) =>
          buttons[index] && !lastButtons[playerIndex][index];

        const axisStates: [boolean, boolean, boolean, boolean] = [
          axes[1] < -DEADZONE,
          axes[1] > DEADZONE,
          axes[0] < -DEADZONE,
          axes[0] > DEADZONE,
        ];

        const axisPressed = (index: number) =>
          axisStates[index] && !lastAxes[playerIndex][index];

        if (wasPressed(12) || axisPressed(0)) navigate('up');
        if (wasPressed(13) || axisPressed(1)) navigate('down');
        if (wasPressed(14) || axisPressed(2)) navigate('left');
        if (wasPressed(15) || axisPressed(3)) navigate('right');

        if (wasPressed(0)) handleSelect();
        if (wasPressed(9)) handleSelect();
        if (wasPressed(1)) toggleDescription();

        lastButtons[playerIndex] = [...buttons];
        lastAxes[playerIndex] = [...axisStates];
      }

      animationId = requestAnimationFrame(pollGamepad);
    };

    animationId = requestAnimationFrame(pollGamepad);
    return () => cancelAnimationFrame(animationId);
  }, [navigate, handleSelect, toggleDescription, hidSnapshotsRef]);

  return (
    <>
      {hidSupported && hidCount < 2 && (
        <ConnectControllerButton onConnect={hidConnect} />
      )}
      <div className="theme-ph26 flex h-screen w-screen items-center justify-center overflow-hidden bg-black">
        <div
          className="relative aspect-video max-h-screen w-full max-w-[177.778vh] overflow-hidden bg-background"
          style={{
            boxShadow:
              'inset 0 0 0 4px hsl(var(--primary)), 0 0 60px rgba(0, 0, 0, 0.5)',
          }}
        >
          <div className="flex h-full">
            <div className="flex flex-1 flex-col p-6">
              <div className="mb-6">
                <h1 className="flex items-center gap-2 font-bold font-logo text-3xl text-primary lowercase tracking-tighter">
                  <span>
                    <span className="font-light">platanus hack</span>{' '}
                    <span className="font-medium">[26]</span>
                  </span>
                  <span className="bg-primary px-2 py-0.5 font-medium text-background">
                    arcade
                  </span>
                </h1>
              </div>

              <div className="relative flex-1 overflow-y-auto">
                <div className="grid auto-rows-min grid-cols-6 gap-4 p-2">
                  {games.map((game, index) => {
                    const isSelected = index === selectedIndex;

                    return (
                      <button
                        key={game.id}
                        ref={isSelected ? selectedTileRef : undefined}
                        type="button"
                        onClick={() => {
                          setSelectedIndex(index);
                          playClickSound();
                        }}
                        onDoubleClick={handleSelect}
                        className={`relative aspect-[4/3] overflow-hidden border-2 transition-all ${
                          isSelected
                            ? 'animate-pulse-glow border-primary bg-primary/10'
                            : 'border-primary/30 bg-background hover:border-primary/60'
                        }`}
                      >
                        <div className="absolute top-1 left-1 z-10 rounded bg-black/70 px-1 py-0.5 font-mono text-[10px] text-white leading-none">
                          {game.playerMode === 'two_player' ? '2P' : '1P'}
                        </div>

                        <div className="absolute right-1 bottom-1 z-10 rounded bg-black/70 px-1 py-0.5 font-mono text-[10px] text-white leading-none">
                          {game.edition.slug.toUpperCase()} {game.edition.flag}
                        </div>

                        <div className="absolute inset-0 overflow-hidden bg-primary/10">
                          {game.coverUrl ? (
                            <img
                              src={game.coverUrl}
                              alt={game.title}
                              className="absolute inset-0 h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <span className="px-1 text-center font-bold text-primary text-xs">
                                {game.title}
                              </span>
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-4 flex items-center justify-center gap-6 border-primary/30 border-t pt-4 font-mono text-primary/60 text-xs">
                <span>A/START: Play</span>
              </div>
            </div>

            <div className="flex min-h-0 w-80 flex-col border-primary border-l-4 bg-background">
              {selectedGame && (
                <>
                  <div className="shrink-0 border-primary border-b-2 p-4">
                    <h2 className="font-bold text-primary text-xl">
                      {selectedGame.title}
                    </h2>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-lg">
                        {selectedGame.edition.flag}
                      </span>
                      <span className="text-primary/80 text-sm">
                        {selectedGame.edition.label}
                      </span>
                      <span className="rounded border border-primary/40 px-1.5 py-0.5 font-mono text-primary text-xs">
                        {selectedGame.playerMode === 'two_player'
                          ? '2 Players'
                          : '1 Player'}
                      </span>
                    </div>
                  </div>

                  <div className="min-h-0 flex-1 overflow-y-auto p-4">
                    <div className="space-y-4">
                      {selectedGame.gameplayPreviewUrl ? (
                        <div className="relative aspect-[4/3] w-full overflow-hidden border-2 border-primary">
                          <video
                            key={selectedGame.id}
                            src={selectedGame.gameplayPreviewUrl}
                            poster={selectedGame.gameplayPosterUrl ?? undefined}
                            autoPlay
                            muted
                            loop
                            playsInline
                            preload="auto"
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                        </div>
                      ) : (
                        selectedGame.coverUrl && (
                          <div className="relative aspect-[4/3] w-full overflow-hidden border-2 border-primary">
                            <img
                              src={selectedGame.coverUrl}
                              alt={selectedGame.title}
                              className="absolute inset-0 h-full w-full object-cover"
                            />
                          </div>
                        )
                      )}

                      <div>
                        <h3 className="mb-1 font-semibold text-primary text-sm">
                          Author
                        </h3>
                        <p className="text-sm">{selectedGame.githubUsername}</p>
                      </div>

                      {selectedGame.description && (
                        <div>
                          <h3 className="mb-1 font-semibold text-primary text-sm">
                            Description
                          </h3>
                          <p className="text-sm leading-relaxed">
                            {showFullDescription ||
                            selectedGame.description.length <=
                              MAX_DESCRIPTION_LENGTH
                              ? selectedGame.description
                              : `${selectedGame.description.slice(0, MAX_DESCRIPTION_LENGTH)}...`}
                          </p>
                          {selectedGame.description.length >
                            MAX_DESCRIPTION_LENGTH && (
                            <button
                              type="button"
                              onClick={toggleDescription}
                              className="mt-2 font-mono text-primary/60 text-xs hover:text-primary"
                            >
                              {showFullDescription
                                ? '[B] Show less'
                                : '[B] Show full description'}
                            </button>
                          )}
                        </div>
                      )}

                      <div>
                        <h3 className="mb-1 font-semibold text-primary text-sm">
                          Code Size
                        </h3>
                        <p className="font-mono text-sm">
                          {(selectedGame.codeSize / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
